import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NotifierService } from 'angular-notifier';
import { IChainMenu, IMenu, UpdateChainMenuPayload, UpdateMenuPayload } from '../../interfaces/menu';
import { tap } from 'rxjs/operators';
import { createHttpParams } from '../../shared/function';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
  providedIn: 'root',
})
export class MenuService {
  public language = localStorage.getItem('lang') ?? this.translate.currentLang;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private notifier: NotifierService,
  ) {}

  public addMenu(
    restaurantId: number,
    pdfFileId: string,
    name: string,
    language: string | null,
    happyHours: boolean = false,
    hidden: boolean = false,
    originalId: number | null,
    iconFileId?: string | null,
    bannerFileId?: string | null
  ): Observable<IMenu> {
    const formData = new FormData();
    formData.append('restaurantId', restaurantId.toString());
    formData.append('happyHours', `${happyHours}`);
    formData.append('hidden', `${hidden}`);
    formData.append('name', name);
    formData.append('pdfFileId', pdfFileId);

    if (originalId) {
      formData.append('originalId', originalId.toString());
    }

    if (language) {
      formData.append('language', language);
    }

    if (iconFileId) {
      formData.append('iconFileId', iconFileId);
    }

    if (bannerFileId) {
      formData.append('bannerFileId', bannerFileId);
    }

    return this.http.post<IMenu>(`/restaurants/${restaurantId}/menus`, formData).pipe(
      tap(() => {
        this.notifier.notify('success', this.translate.instant('pages.menu.messages.uploadMenu'));
      })
    );
  }

  public getRestaurantMenus({
    restaurantId,
    sort = 'createdAt',
    order = 'DESC',
    menuType = '',
  }: {
    restaurantId: number;
    sort?: string;
    order?: string;
    menuType?: string;
  }): Observable<IMenu[]> {
    const params = createHttpParams({ restaurantId, sort, order, menuType });

    return this.http.get<IMenu[]>(`/restaurants/${restaurantId}/menus`, {
      params,
    });
  }

  public getRestaurantMenuById(restaurantId: number, menuId: number): Observable<IMenu> {
    return this.http.get<IMenu>(`/restaurants/${restaurantId}/menus/${menuId}`);
  }

  public updateMenu(
    restaurantId: number,
    menuId: number,
    name: string,
    language: string | null,
    happyHours: boolean,
    hidden: boolean,
    menuFileId: string | null,
    iconFileId?: string | null,
    bannerFileId?: string | null
  ): Observable<IMenu> {
    const body: UpdateMenuPayload = {
      name,
      language,
      happyHours,
      hidden,
    };

    if (menuFileId) {
      body.pdfFileId = menuFileId;
    }

    if (iconFileId) {
      body.iconFileId = iconFileId;
    }

    if (bannerFileId) {
      body.bannerFileId = bannerFileId;
    }

    return this.http
      .patch<IMenu>(`/restaurants/${restaurantId}/menus/${menuId}`, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        tap(() => {
          this.notifier.notify('success', this.translate.instant('pages.menu.messages.updateMenu'));
        })
      );
  }

  public getChainMenus({
    chainId,
    sort = 'createdAt',
    order = 'DESC',
    menuType = '',
  }: {
    chainId: number;
    sort?: string;
    order?: string;
    menuType?: string;
  }): Observable<IChainMenu[]> {
    const params = createHttpParams({ sort, order, menuType });

    return this.http.get<IChainMenu[]>(`/chains/${chainId}/menus`, {
      params,
    });
  }

  public getChainMenuById(chainId: number, menuId: number): Observable<IChainMenu> {
    return this.http.get<IChainMenu>(`/chains/${chainId}/menus/${menuId}`);
  }

  public addChainMenu(
    chainId: number,
    pdfFileId: string,
    name: string,
    language: string | null,
    happyHours: boolean = false,
    hidden: boolean = false,
    originalId: number | null,
    selectedRestaurants: number[] | null,
    iconFileId: string | null,
    bannerFileId: string | null
  ): Observable<IChainMenu> {
    const formData = new FormData();
    formData.append('chainId', chainId?.toString());
    formData.append('happyHours', `${happyHours}`);
    formData.append('hidden', `${hidden}`);
    formData.append('name', name);
    formData.append('restaurantIdsToAttach', JSON.stringify(selectedRestaurants));
    formData.append('pdfFileId', pdfFileId);

    if (originalId) {
      formData.append('originalId', originalId.toString());
    }

    if (language) {
      formData.append('language', language);
    }

    if (iconFileId) {
      formData.append('iconFileId', iconFileId);
    }

    if (bannerFileId) {
      formData.append('bannerFileId', bannerFileId);
    }

    return this.http.post<IChainMenu>(`/chains/${chainId}/menus`, formData).pipe(
      tap(() => {
        this.notifier.notify('success', this.translate.instant('pages.menu.messages.uploadMenu'));
      })
    );
  }

  public updateChainMenu(
    menuId: number,
    chainId: number,
    name: string,
    language: string | null,
    happyHours: boolean,
    hidden: boolean,
    selectedRestaurants: number[] | null,
    menuFileId: string | null,
    iconFileId: string | null,
    bannerFileId: string | null
  ): Observable<IChainMenu> {
    const body: UpdateChainMenuPayload = {
      chainId,
      name,
      language,
      happyHours,
      hidden,
      restaurantIdsToAttach: selectedRestaurants ? selectedRestaurants : null,
    };

    if (menuFileId) {
      body.pdfFileId = menuFileId;
    }

    if (iconFileId) {
      body.iconFileId = iconFileId;
    }

    if (bannerFileId) {
      body.bannerFileId = bannerFileId;
    }

    return this.http
      .patch<IChainMenu>(`/chains/${chainId}/menus/${menuId}`, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        tap(() => {
          this.notifier.notify('success', this.translate.instant('pages.menu.messages.updateMenu'));
        })
      );
  }

  public deleteRestaurantMenu(restaurantId: number, menuId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/restaurants/${restaurantId}/menus/${menuId}`).pipe(
      tap(() => {
        this.notifier.notify('success', this.translate.instant('pages.menu.messages.deletedMenu'));
      })
    );
  }

  public deleteChainMenu(chainId: number, menuId: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/chains/${chainId}/menus/${menuId}`).pipe(
      tap(() => {
        this.notifier.notify('success', this.translate.instant('pages.menu.messages.deletedMenu'));
      })
    );
  }
}
